import * as ko from "knockout";
import { isSubscribable } from "knockout";

import { buttons, type DatepickerOptions, datepicker } from "../../lib/flatpickr";
import type { CheckExtended } from "../extensions/invalid";

interface PickerOptions {
    enableTime?: boolean; // Enable the time picker.
    isoFormat?: boolean; // Show the formatted date to the user, but store / load it as ISO8601.
    maxDate?: Date | string; // The maximum selectable date. When set to null, there is no maximum.
    minDate?: Date | string; // The minimum selectable date. When set to null, there is no minimum.
    onValueUpdate?: () => void;
}

interface ElementAttributes {
    id?: string;
    name?: string;
    placeholder?: string;
}

interface Params {
    value: CheckExtended<ko.Observable<string>>;
    id?: string;
    name?: string;
    class?: string;
    placeholder?: string;
    clearButton?: boolean;
    css?: Record<string, string | ko.MaybeObservable<boolean>>;
    enabled?: ko.MaybeObservable<boolean>;
    options?: PickerOptions;
    valueUpdate?: "afterkeydown" | "input";
}

interface AnchorElementAttributes {
    id?: string;
    title?: string;
}

interface AnchorCalendarPopupParams {
    value: CheckExtended<ko.Observable<string>>;
    attr?: AnchorElementAttributes;
    class?: string;
    css?: Record<string, string | ko.MaybeObservable<boolean>>;
    enabled?: ko.MaybeObservable<boolean>;
    options?: PickerOptions;
    placeholder?: string;
}

/**
 * Calendar Popup
 *
 * Provides datepicker (input field) using the jquery ui library.
 *
 * @property element - The associated date picker <input> field.
 *
 * @property class - Additional class name(s) that will be assigned to the date picker <input> field.
 *
 * @property css - JavaScript object with additional class name(s) as property, that will be assigned to
 *                 the date picker <input> field according to their values evaluate to true or false.
 *
 * @property enabled - if false, the date picker <input> field will be disabled (true by default).
 *
 * @property id - Specifies the id (attribute) of the date picker <input> field.
 *
 * @property name - Specifies the name (attribute) of the date picker <input> field.
 *
 * @property options - Additional options that will be passed to the date picker instance (@see PickerOptions).
 *
 * @property placeholder - Hint text that will be displayed in the date picker <input> field before user selects a value.
 *
 * @property clearButton - Show a button to clear the date picker <input> field.
 *
 * @property value - The associated date picker <input> field’s value.
 *
 * @property valueUpdate - Defines additional browser events knockout should use to detect changes besides the change event.
 *                        (e.g.: "input", "keyup", "keypress" or "afterkeydown")
 *
 * @example:
 *   <body>
 *      <ko-calendar-popup params="value: dateFrom,
 *                                 name: 'from_date',
 *                                 class: 'width_full',
 *                                 css: {invalid_data: dateFrom.isInvalid},
 *                                 options: {maxDate: 'today'}"></ko-calendar-popup>
 *   </body>
 */
class CalendarPopupViewModel {
    private readonly params: Params;
    private readonly elementAttributes: ElementAttributes;

    constructor(params: Params, componentElement: HTMLInputElement) {
        this.params = params;
        this.elementAttributes = {};

        if (params.id) {
            this.elementAttributes.id = params.id;
        }

        if (params.name) {
            this.elementAttributes.name = params.name;
        }

        if (params.placeholder) {
            this.elementAttributes.placeholder = params.placeholder;
        }

        const datepickerOptions: DatepickerOptions = {
            buttons: [buttons.today, ...(params.clearButton ? [buttons.clear] : []), buttons.close],
            allowInput: true,
            minDate: params?.options?.minDate,
            maxDate: params?.options?.maxDate,
            isoFormat: params?.options?.isoFormat,
            enableTime: params?.options?.enableTime,
        };

        setTimeout(() => {
            const picker = datepicker(componentElement, datepickerOptions);

            // follow changes to the bound value observable
            if (ko.isObservable(params.value)) {
                ko.computed(
                    () => {
                        if (picker.setDate) {
                            picker.setDate(params.value());
                        }
                    },
                    null,
                    { disposeWhenNodeIsRemoved: componentElement },
                );
            }
            if (isSubscribable(params.enabled)) {
                params.enabled.subscribe((enabled) => {
                    if (enabled) {
                        picker._input.removeAttribute("disabled");
                    } else {
                        picker._input.setAttribute("disabled", "disabled");
                    }
                });
            }

            // hide/destroy datepicker when corresponding node element is removed
            ko.utils.domNodeDisposal.addDisposeCallback(componentElement, () => {
                picker.destroy();
            });
        }, 0);
    }
}

class AnchorCalendarPopupViewModel {
    private readonly elementAttributes: AnchorElementAttributes = {};
    public readonly params: Params;

    constructor(params: AnchorCalendarPopupParams, componentElement: HTMLAnchorElement) {
        this.params = params;

        if (params.attr?.id) {
            this.elementAttributes.id = params.attr.id;
        }

        if (params.attr?.title) {
            this.elementAttributes.title = params.attr.title;
        }

        setTimeout(() => {
            const picker = datepicker(componentElement, {
                buttons: [buttons.today, buttons.close],
                minDate: params.options?.minDate,
                maxDate: params.options?.maxDate,
                isoFormat: params.options?.isoFormat,
                enableTime: params.options?.enableTime,
                onValueUpdate: params.options?.onValueUpdate,
            });

            // follow changes to the bound value observable
            if (ko.isObservable(params.value)) {
                ko.computed(
                    () => {
                        if (picker.setDate) {
                            picker.setDate(params.value());
                        }
                    },
                    null,
                    { disposeWhenNodeIsRemoved: componentElement },
                );
            }
            if (isSubscribable(params.enabled)) {
                params.enabled.subscribe((enabled) => {
                    if (enabled) {
                        picker._input.removeAttribute("disabled");
                    } else {
                        picker._input.setAttribute("disabled", "disabled");
                    }
                });
            }

            // hide/destroy datepicker when corresponding node element is removed
            ko.utils.domNodeDisposal.addDisposeCallback(componentElement, () => picker.destroy());
        }, 0);
    }
}

export const getCalendarPopupComponent = () => ({
    viewModel: {
        createViewModel: (params: any, componentInfo: ko.components.ComponentInfo) => {
            const htmlElement = componentInfo.element as HTMLElement;
            if (htmlElement.nodeName === "#comment") {
                throw new Error("Only use this component through <ko-calendar-popup/> HTML tag.");
            }
            return new CalendarPopupViewModel(params, htmlElement.firstElementChild as HTMLInputElement);
        },
    },
    template: `
            <input type="text"
                   data-bind="value: params.value,
                              valueUpdate: params.valueUpdate,
                              attr: elementAttributes,
                              class: params.class || '',
                              css: params.css || {},
                              enable: params.enabled !== undefined ? params.enabled: true">
        `,
});

export const getAnchorCalendarPopupComponent = () => ({
    viewModel: {
        createViewModel: (params: any, componentInfo: ko.components.ComponentInfo) => {
            const htmlElement = componentInfo.element as HTMLElement;
            if (htmlElement.nodeName === "#comment") {
                throw new Error("Only use this component through <ko-calendar-popup-anchor/> HTML tag.");
            }
            return new AnchorCalendarPopupViewModel(params, htmlElement.firstElementChild as HTMLAnchorElement);
        },
    },
    template: `
        <a data-bind="text: params.value ? params.value : (params.placeholder || translated('None')),
                      value: params.value,
                      css: params.css || {},
                      attr: elementAttributes,
                      class: params.class || '',
                      valueUpdate: params.valueUpdate,
                      enable: params.enabled !== undefined ? params.enabled: true"></a>
    `,
});
